import { Icon } from "@components/atoms/Icons"
import { AddressGroup } from "@components/ui/AddressGroup"
import { Captcha } from "@components/ui/Captcha"
import { CheckboxGroup } from "@components/ui/CheckboxGroup"
import { DateField } from "@components/ui/Date"
import { Input } from "@components/ui/Input"
import { NameGroup } from "@components/ui/NameGroup"
import { RadioGroup } from "@components/ui/RadioGroup"
import { Select } from "@components/ui/Select"
import { TextArea } from "@components/ui/TextArea"
import { TimeField } from "@components/ui/Time"
import {
  GFAddressData,
  GFAddressOptions,
  GFConsentOptions,
  GFFieldComponentProps,
  GFFieldComponentSet,
  GFFileUploadOptions,
  GFGenericTextOptions,
  GFNameData,
  GFNameOptions,
  GFNumberOptions,
  GFSelectOptions,
  GFTextOptions,
  GravityFormsFieldState,
} from "eddev/integrations/gravityforms"
import { useRoute } from "eddev/routing"
import { PropsWithChildren, useEffect, useId, useRef, useState } from "react"

export const FIELD_TYPES: GFFieldComponentSet = {}

function FieldWrapper(
  props: PropsWithChildren<{
    isRequired?: boolean
    showLabel?: boolean
    label?: string
    labelId?: string
    error?: string
  }>,
) {
  return (
    <div className="flex flex-col gap-3">
      {props.showLabel && (
        <label htmlFor={props.labelId} className={`type-label-m ${props.error ? `text-error` : `text-text`}`}>
          {props.label} {props.isRequired ? "*" : null}
        </label>
      )}
      {props.children}

      {props.error && (
        <p
          className={`type-label-s ${props.error ? `text-error` : `text-text`}`}
          dangerouslySetInnerHTML={{ __html: props.error }}
        />
      )}
    </div>
  )
}

FIELD_TYPES["text"] = (props: GravityFormsFieldState<string, GFTextOptions>) => {
  const id = useId()

  const route = useRoute()
  const [defaultValue, setDefaultValue] = useState<string | null>(null)
  useEffect(() => {
    if (props.allowsPrepopulate) {
      if (typeof route.query[props.inputName] === "string") {
        setDefaultValue(route.query[props.inputName] as string)
      }
    }
  }, [])

  return (
    <FieldWrapper labelId={id} {...props}>
      <Input
        defaultValue={defaultValue ? defaultValue : props.value}
        onChange={(e: any) => props.set(e.currentTarget.value)}
        id={id}
        type={props.type}
        required={props.isRequired}
        disabled={props.disabled}
        placeholder={`${props?.placeholder}`}
      />
    </FieldWrapper>
  )
}

FIELD_TYPES["textarea"] = (props: GravityFormsFieldState<string, GFTextOptions> & { dark?: boolean }) => {
  const id = useId()

  const route = useRoute()
  const [defaultValue, setDefaultValue] = useState<string | null>(null)
  useEffect(() => {
    if (props.allowsPrepopulate) {
      if (typeof route.query[props.inputName] === "string") {
        setDefaultValue(route.query[props.inputName] as string)
      }
    }
  }, [])

  return (
    <FieldWrapper {...props}>
      <TextArea
        defaultValue={defaultValue ? defaultValue : props.value}
        onChange={(e: any) => props.set(e.currentTarget.value)}
        id={id}
        label={props.label}
        required={props.isRequired}
        disabled={props.disabled}
        placeholder={`${props?.placeholder}`}
      />
      {props.description && (
        <span className="type-label-m text-accent" dangerouslySetInnerHTML={{ __html: props.description }} />
      )}
    </FieldWrapper>
  )
}

FIELD_TYPES["select"] = (props: GravityFormsFieldState<string, GFSelectOptions>) => {
  const id = useId()

  return (
    <FieldWrapper {...props} labelId={id}>
      <Select
        value={props.value ?? ""}
        id={id}
        required={props.isRequired}
        disabled={props.disabled}
        choices={props.choices}
        onChange={(e: any) => props.set(e.currentTarget.value)}
      />
    </FieldWrapper>
  )
}

FIELD_TYPES["multiselect"] = (props: GravityFormsFieldState<string[], GFSelectOptions>) => {
  const id = useId()

  return (
    <FieldWrapper {...props} labelId={id}>
      <Select
        id={id}
        aria-label={props.label}
        onChange={(e: any) => {
          props.set(Array.from(e.target.selectedOptions, (option: any) => option.value))
        }}
        multiple={true}
        choices={props.choices}
        required={props.required}
        disabled={props.disabled}
      />
    </FieldWrapper>
  )
}

FIELD_TYPES["checkbox"] = (props: GravityFormsFieldState<string[], GFSelectOptions>) => {
  const id = useId()

  return (
    <FieldWrapper {...props}>
      <CheckboxGroup {...props} />
    </FieldWrapper>
  )
}

FIELD_TYPES["consent"] = (props: GravityFormsFieldState<boolean, GFConsentOptions>) => {
  const id = useId()

  return (
    <FieldWrapper {...props}>
      <input
        id={id}
        type="checkbox"
        required={props.isRequired}
        disabled={props.disabled}
        checked={props.value ?? false}
        onChange={(e) => props.set(e.currentTarget.checked)}
        className="hidden [&:checked+label>div>div]:flex"
      />

      <label htmlFor={id} className="type-label-m text-text cursor-pointer flex items-center gap-4 py-2">
        <div className="w-5 h-5 flex items-center justify-center border">
          <div className="w-3 h-3 hidden bg-text" />
        </div>
        <span dangerouslySetInnerHTML={{ __html: props.checkboxLabel }} />
      </label>

      {props?.description && (
        <p
          className="type-label-s text-text [&>a]:underline [&>a]:hover:no-underline"
          dangerouslySetInnerHTML={{ __html: props.description }}
        />
      )}
    </FieldWrapper>
  )
}

FIELD_TYPES["radio"] = (props: GravityFormsFieldState<string, GFSelectOptions>) => {
  return (
    <FieldWrapper {...props}>
      <RadioGroup {...props} />
    </FieldWrapper>
  )
}

export type GFImageChoice = {
  text: string
  value: string
  file_url?: string
}

export type GFChoiceFieldState = GravityFormsFieldState<
  string | string[], // could be single or multiple
  GFSelectOptions & {
    choices: GFImageChoice[]
    choiceLimit?: "single" | "exactly" | "range"
    choiceLimitMin?: string
    choiceLimitMax?: string
    choiceLimitNumber?: string
  }
>

FIELD_TYPES["multi_choice"] = (props: GFChoiceFieldState) => {
  return (
    <FieldWrapper {...props}>
      {props.choiceLimit === "exactly" && (
        <p className="type-label-s text-text [&>a]:underline [&>a]:hover:no-underline">
          Select exactly {props.choiceLimitNumber}.
        </p>
      )}
      {props.choiceLimit === "range" && (
        <p className="type-label-s text-text [&>a]:underline [&>a]:hover:no-underline">
          {props.choiceLimitMax === "0" && <>Select at least {props.choiceLimitMin}.</>}
          {props.choiceLimitMin === "0" && <>Select up to {props.choiceLimitMax}.</>}
          {props.choiceLimitMin !== "0" && props.choiceLimitMax !== "0" && (
            <>
              Select between {props.choiceLimitMin} and {props.choiceLimitMax}.
            </>
          )}
        </p>
      )}
      {props.choiceLimit === "single" ? (
        <RadioGroup {...(props as GFFieldComponentProps<"radio">)} />
      ) : (
        <CheckboxGroup {...(props as GFFieldComponentProps<"checkbox">)} />
      )}
    </FieldWrapper>
  )
}

FIELD_TYPES["image_choice"] = (props: GFChoiceFieldState) => {
  return (
    <FieldWrapper {...props}>
      {props.choiceLimit === "exactly" && (
        <p className="type-label-s text-text [&>a]:underline [&>a]:hover:no-underline">
          Select exactly {props.choiceLimitNumber}.
        </p>
      )}
      {props.choiceLimit === "range" && (
        <p className="type-label-s text-text [&>a]:underline [&>a]:hover:no-underline">
          {props.choiceLimitMax === "0" && <>Select at least {props.choiceLimitMin}.</>}
          {props.choiceLimitMin === "0" && <>Select up to {props.choiceLimitMax}.</>}
          {props.choiceLimitMin !== "0" && props.choiceLimitMax !== "0" && (
            <>
              Select between {props.choiceLimitMin} and {props.choiceLimitMax}.
            </>
          )}
        </p>
      )}
      {props.choiceLimit === "single" ? (
        <RadioGroup {...(props as GFFieldComponentProps<"radio">)} />
      ) : (
        <CheckboxGroup {...(props as GFFieldComponentProps<"checkbox">)} />
      )}
    </FieldWrapper>
  )
}

FIELD_TYPES["email"] = (props: GravityFormsFieldState<string, GFGenericTextOptions>) => {
  const id = useId()

  const route = useRoute()
  const [defaultValue, setDefaultValue] = useState<string | null>(null)
  useEffect(() => {
    if (props.allowsPrepopulate) {
      if (typeof route.query[props.inputName] === "string") {
        setDefaultValue(route.query[props.inputName] as string)
      }
    }
  }, [])

  return (
    <FieldWrapper {...props} labelId={id}>
      <Input
        type="email"
        defaultValue={defaultValue ? defaultValue : props.value}
        onChange={(e: any) => props.set(e.currentTarget.value)}
        id={id}
        label={props.label}
        required={props.isRequired}
        disabled={props.disabled}
        placeholder={`${props?.placeholder}`}
      />
    </FieldWrapper>
  )
}
FIELD_TYPES["fileupload"] = (props: GravityFormsFieldState<File | FileList | File[], GFFileUploadOptions>) => {
  const id = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  const [previews, setPreviews] = useState<File[]>([])
  const [errors, setErrors] = useState<string[]>([])

  // Pull settings from props
  const { maxFileSize = 5, maxFiles = 1, multipleFiles = false, allowedExtensions = [] } = props

  // Normalize empty values like [""] to []
  const normalizedAllowedExtensions = Array.isArray(allowedExtensions)
    ? allowedExtensions.filter((ext) => ext && ext.trim() !== "")
    : []

  // Disallowed executable/code-based types (you can expand this list)
  const disallowedExtensions = ["exe", "bat", "js", "php", "sh", "py", "pl", "rb", "cgi"]
  const disallowedMimeTypes = [
    "application/x-msdownload", // exe
    "application/javascript", // js
    "application/x-sh", // shell
    "text/x-python", // py
    "text/x-perl", // pl
    "text/x-php", // php
  ]

  // Get MIME types if extensions are explicitly allowed
  const allowedMimeTypes = normalizedAllowedExtensions.length
    ? normalizedAllowedExtensions.map((ext) => {
        switch (ext.toLowerCase()) {
          case "jpg":
          case "jpeg":
            return "image/jpeg"
          case "png":
            return "image/png"
          case "gif":
            return "image/gif"
          case "pdf":
            return "application/pdf"
          default:
            return `application/${ext.toLowerCase()}`
        }
      })
    : null // fallback to "allow everything except code"

  const validateFiles = (files: FileList): File[] => {
    const validFiles: File[] = []
    const newErrors: string[] = []

    const allFiles = Array.from(files)

    if (multipleFiles && allFiles.length > maxFiles) {
      newErrors.push(`❌ You can only upload up to ${maxFiles} files`)
    }

    const filesToValidate = multipleFiles ? allFiles.slice(0, maxFiles) : [allFiles[0]]

    for (const file of filesToValidate) {
      const sizeMB = file.size / 1024 / 1024
      const ext = file.name.split(".").pop()?.toLowerCase() || ""
      const isBlockedType = disallowedExtensions.includes(ext) || disallowedMimeTypes.includes(file.type)

      const isValidType = allowedMimeTypes ? allowedMimeTypes.includes(file.type) : !isBlockedType

      if (!isValidType) {
        newErrors.push(`❌ ${file.name} is not an allowed file type`)
        continue
      }

      if (sizeMB > maxFileSize) {
        newErrors.push(`❌ ${file.name} is too large (${sizeMB.toFixed(1)} MB)`)
        continue
      }

      validFiles.push(file)
    }

    setErrors(newErrors)
    return validFiles
  }

  const handleFiles = (files: FileList) => {
    const newValidFiles = validateFiles(files)
    if (newValidFiles.length === 0) return

    // Append new files to the current list (while avoiding duplicates if needed)
    const combined = [...previews, ...newValidFiles]

    // Enforce maxFiles limit
    const limited = combined.slice(0, maxFiles)

    setPreviews(limited)
    props.set(multipleFiles ? limited : limited[0])
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    if (e.dataTransfer.files) handleFiles(e.dataTransfer.files)
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
  }

  const acceptString = normalizedAllowedExtensions.length
    ? normalizedAllowedExtensions.map((ext) => `.${ext}`).join(",")
    : undefined

  return (
    <FieldWrapper {...props} labelId={id}>
      <div
        className="flex flex-col items-center gap-4 justify-center w-full h-40 p-9 border-2 border-dashed rounded-lg cursor-pointer transition bg-bgAlt hover:bg-bg text-text"
        onClick={() => inputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        <Icon name="ARROW_UP" />
        <p className="type-body-m">Drag and drop files here</p>
        <p className="type-label-s">
          or click to upload (Max {maxFiles} file{maxFiles > 1 ? "s" : ""}, {maxFileSize}MB each)
        </p>
        <input
          id={id}
          ref={inputRef}
          type="file"
          multiple={multipleFiles}
          required={props.isRequired}
          disabled={props.disabled}
          accept={acceptString}
          onChange={(e) => {
            if (e.currentTarget.files) handleFiles(e.currentTarget.files)
          }}
          className="hidden"
        />
      </div>

      {previews.length > 0 && (
        <ul className="mt-2 text-text">
          {previews.map((file, index) => (
            <li key={index} className="flex items-center justify-between">
              <span className="type-label-m">{file.name}</span>
              <div className="flex items-center gap-2">
                <span className="type-label-s uppercase">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                <button
                  type="button"
                  onClick={() => {
                    const updated = previews.filter((_, i) => i !== index)
                    setPreviews(updated)
                    props.set(multipleFiles ? updated : (updated[0] ?? null))
                  }}
                  className="type-label-s uppercase0 hover:underline"
                >
                  Remove
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {errors.length > 0 && (
        <div className="mt-2 flex flex-col gap-2">
          {errors.map((err, i) => (
            <p className="type-label-m text-accent" key={i}>
              {err}
            </p>
          ))}
        </div>
      )}
    </FieldWrapper>
  )
}

FIELD_TYPES["number"] = (props: GravityFormsFieldState<number, GFNumberOptions>) => {
  const id = useId()

  return (
    <FieldWrapper {...props} labelId={id}>
      <Input
        type="number"
        value={props.value ?? ""}
        onChange={(e: any) => props.set(e.currentTarget.valueAsNumber)}
        id={id}
        required={props.isRequired}
        disabled={props.disabled}
        placeholder={props.placeholder}
        size={props.size}
      />
    </FieldWrapper>
  )
}

FIELD_TYPES["html"] = (props: GravityFormsFieldState<void, {}>) => {
  return <div className="w-full" content={props.content} />
}

FIELD_TYPES["section"] = (props: GravityFormsFieldState<void, {}>) => {
  return (
    <div className="w-full flex flex-col my-5 py-3 border-b border-text">
      <h2 className="type-title-m text-text">
        {props.label}{" "}
        {props.isRequired ? (
          <>
            <i>*</i>
          </>
        ) : (
          ``
        )}
      </h2>
    </div>
  )
}

FIELD_TYPES["name"] = (props: GravityFormsFieldState<GFNameData, GFNameOptions>) => {
  return (
    <FieldWrapper {...props}>
      <NameGroup {...props} />
    </FieldWrapper>
  )
}

FIELD_TYPES["date"] = (props: GravityFormsFieldState<string, {}>) => {
  const id = useId()

  return (
    <FieldWrapper {...props} labelId={id}>
      <DateField value={props.value} onValueChange={(date) => props.set(date!)} id={id} />
    </FieldWrapper>
  )
}

FIELD_TYPES["time"] = (props: GravityFormsFieldState<string, {}>) => {
  const id = useId()

  return (
    <FieldWrapper {...props} labelId={id}>
      <TimeField value={props.value} onValueChange={(value) => props.set(value!)} id={id} />
    </FieldWrapper>
  )
}

FIELD_TYPES["phone"] = (props: GravityFormsFieldState<string, GFGenericTextOptions>) => {
  const id = useId()

  return (
    <FieldWrapper {...props} labelId={id}>
      <Input
        defaultValue={props.value}
        onChange={(e: any) => props.set(e.currentTarget.value)}
        id={id}
        type="tel"
        required={props.isRequired}
        disabled={props.disabled}
        placeholder={`${props?.placeholder}`}
        autoComplete="tel"
      />
    </FieldWrapper>
  )
}

FIELD_TYPES["address"] = (props: GravityFormsFieldState<GFAddressData, GFAddressOptions>) => {
  return (
    <FieldWrapper {...props}>
      <AddressGroup {...props} />
    </FieldWrapper>
  )
}

FIELD_TYPES["website"] = (props: GravityFormsFieldState<string, GFGenericTextOptions>) => {
  const id = useId()

  return (
    <FieldWrapper {...props} labelId={id}>
      <Input
        type="url"
        defaultValue={props.value}
        onChange={(e: any) => props.set(e.currentTarget.value)}
        id={id}
        required={props.isRequired}
        disabled={props.disabled}
        placeholder={props?.placeholder}
      />
    </FieldWrapper>
  )
}

FIELD_TYPES["captcha"] = (props: GravityFormsFieldState<any, {}>) => {
  return (
    <FieldWrapper {...props}>
      <Captcha {...props} />
    </FieldWrapper>
  )
}

// FIELD_TYPES["list"] = (props: GravityFormsFieldState<any, {}>) => {
//   console.log(props)
//   return (
//     <FieldWrapper {...props}>
//       <div className="w-full flex flex-col gap-4">
//         {props.choices ? (
//           <>
//             <div className="w-full flex gap-3">
//               {props.choices.map((choice: any, i: number) => (
//                 <div className="flex flex-col gap-1" key={i}>
//                   <label className="type-label-s text-text">{choice.text}</label>
//                   <Input
//                     defaultValue={choice.value}
//                     // onChange={(e: any) => props.set(e.currentTarget.value)}

//                     type={choice.type}
//                     required={choice.isRequired}
//                     disabled={choice.disabled}
//                     placeholder={`${choice?.placeholder}`}
//                   />
//                 </div>
//               ))}
//             </div>
//           </>
//         ) : (
//           <></>
//         )}
//       </div>
//     </FieldWrapper>
//   )
// }

// FIELD_TYPES["page"] = (props: GravityFormsFieldState<any, {}>) => {
//   console.log(props)
//   return <></>
// }
